import React, { useState, useEffect } from "react";
import { Formik } from "formik";
import {
    Drawer,
    Grid,
    Typography,
    Box,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Chip,
    IconButton,
    TextField,
    InputLabel,
    FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";

import Input from "../../Common/Input";
import Button from "../../Common/Button";
import MultipleSelectJson from "../../Common/MultipleSelectJson";
import DatePickerComponent from "../../Common/DatePicker";
import RichTextEditor from "../../Common/editor/RichTextEditor";
import { useDispatch } from "react-redux";
import { setSnackBar } from "../../../store/common/commonSlice";
import {
    useAddNewAlertMutation,
    useUpdateAlertMutation,
    useUploadAlertImageMutation,
} from "../../../store/alerts/alertsApis";
import { useGetUsersDropdownQuery } from "../../../store/users/usersApis";
import { AlertsValidation } from "./AlertsValidation";
import { baseUrl } from "../../../helpers/constants";

export const AlertsAddDrawer = ({ initialValues, show, close, formType }) => {
    const dispatch = useDispatch();
    const [addNewAlert, { isLoading: addAlertLoading }] = useAddNewAlertMutation();
    const [updateAlert, { isLoading: updateAlertLoading }] = useUpdateAlertMutation();
    const [uploadImage, { isLoading: uploadingImage }] = useUploadAlertImageMutation();
    const [uploadedImages, setUploadedImages] = useState(() => {
        // Transform initial images to the expected format
        if (initialValues?.images && initialValues.images.length > 0) {
            return initialValues.images.map((img, index) => ({
                image_url: img.image_url,
                order: img.image_order || index,
            }));
        }
        return [];
    });

    const { data: usersDropdown } = useGetUsersDropdownQuery({});

    const disabled = formType === "View";

    // Update uploadedImages when initialValues change
    useEffect(() => {
        if (initialValues?.images && initialValues.images.length > 0) {
            const transformedImages = initialValues.images.map((img, index) => ({
                image_url: img.image_url,
                order: img.image_order || index,
            }));
            setUploadedImages(transformedImages);
        } else {
            setUploadedImages([]);
        }
    }, [initialValues]);

    const roleOptions = [
        { value: "admin", label: "Admin" },
        { value: "distributor", label: "Distributor" },
        { value: "staff", label: "Staff" },
    ];

    const userOptions =
        usersDropdown?.results?.map((user) => ({
            value: user.id,
            label: `${user.name} (${user.role})`,
        })) || [];

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith("image/")) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: "Please upload an image file",
                    severity: "error",
                }),
            );
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: "Image size should not exceed 5MB",
                    severity: "error",
                }),
            );
            return;
        }

        try {
            const formData = new FormData();
            formData.append("image", file);

            const result = await uploadImage(formData).unwrap();

            console.log("Upload result:", result);

            // Extract the URL from the result object
            const imageUrl = typeof result === "string" ? result : result?.location || result?.image_url || result;

            console.log("Extracted image URL:", imageUrl);

            setUploadedImages([
                ...uploadedImages,
                {
                    image_url: imageUrl,
                    order: uploadedImages.length,
                },
            ]);

            dispatch(
                setSnackBar({
                    open: true,
                    message: "Image uploaded successfully",
                    severity: "success",
                }),
            );
        } catch (error) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: error?.data?.message || "Failed to upload image",
                    severity: "error",
                }),
            );
        }
    };

    const handleRemoveImage = (index) => {
        setUploadedImages(uploadedImages.filter((_, i) => i !== index));
    };

    const onFormSubmit = async (values, resetForm) => {
        try {
            // Transform target_roles and target_users to match backend expectations
            const transformedValues = {
                ...values,
                target_roles: values.target_roles?.map((role) => role.value) || [],
                target_users: values.target_users?.map((user) => user.value) || [],
            };

            const payload = {
                ...transformedValues,
                images: uploadedImages,
            };

            if (formType === "Edit") {
                const updatePayload = { id: initialValues.id, ...payload };
                console.log("Update payload:", updatePayload);
                await updateAlert(updatePayload).unwrap();
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Alert updated successfully",
                        severity: "success",
                    }),
                );
            } else {
                await addNewAlert(payload).unwrap();
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Alert created successfully",
                        severity: "success",
                    }),
                );
            }
            setUploadedImages([]);
            close();
            resetForm();
        } catch (error) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: error?.data?.message || error.message,
                    severity: "error",
                }),
            );
        }
    };

    // Transform initial values for editing
    const getTransformedInitialValues = () => {
        if (!initialValues) {
            return {
                title: "",
                content: "",
                target_type: "all",
                target_roles: [],
                target_users: [],
                expiry_date: "",
                is_active: true,
            };
        }

        // Format date for DatePicker (YYYY-MM-DD format)
        const formatDate = (dateString) => {
            if (!dateString) return "";
            const date = new Date(dateString);
            return date.toISOString().split("T")[0];
        };

        const transformed = {
            title: initialValues.title || "",
            content: initialValues.content || "",
            target_type: initialValues.target_type || "all",
            target_roles:
                initialValues.target_roles?.map((role) =>
                    typeof role === "string"
                        ? { value: role, label: role.charAt(0).toUpperCase() + role.slice(1) }
                        : role,
                ) || [],
            target_users:
                initialValues.target_users?.map((userId) => {
                    const user = userOptions.find((u) => u.value === userId);
                    return user || { value: userId, label: `User ${userId}` };
                }) || [],
            expiry_date: formatDate(initialValues.expiry_date),
            is_active: initialValues.is_active !== undefined ? initialValues.is_active : true,
        };

        console.log("Original initialValues:", initialValues);
        console.log("Transformed initialValues:", transformed);

        return transformed;
    };

    return (
        <Formik
            key={`${formType}-${initialValues?.id || "new"}`}
            enableReinitialize
            initialValues={getTransformedInitialValues()}
            onSubmit={(values, { resetForm }) => {
                onFormSubmit(values, resetForm);
            }}
            validationSchema={AlertsValidation}
            context={{ formType }}
        >
            {({ handleChange, handleBlur, handleSubmit, values, errors, setFieldValue }) => (
                <Drawer
                    anchor={"right"}
                    open={show}
                    PaperProps={{
                        sx: { width: { xs: "100%", md: "50%", sm: "50%", lg: "40%" } },
                    }}
                    onClose={() => {
                        setUploadedImages([]);
                        close();
                    }}
                >
                    <Grid container sx={{ display: "flex" }} direction={"column"} width={"100%"} height={"100%"}>
                        <Grid
                            container
                            flex={0}
                            px={2}
                            py={1}
                            borderBottom={1}
                            borderColor={"rgba(5, 5, 5, 0.06)"}
                            justifyContent={"space-between"}
                        >
                            <Grid item alignSelf={"center"}>
                                <Typography variant="h6">
                                    {formType === "Add"
                                        ? "Create New Alert"
                                        : formType === "Edit"
                                        ? "Update Alert"
                                        : "View Alert"}
                                </Typography>
                            </Grid>
                            <Grid item alignSelf={"center"}>
                                <IconButton
                                    color="default"
                                    onClick={() => {
                                        setUploadedImages([]);
                                        close();
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Grid>
                        </Grid>

                        <Grid flex={1} px={2} py={2} overflow={"auto"}>
                            <Grid container spacing={2}>
                                {/* Title */}
                                <Grid item xs={12}>
                                    <Input
                                        id="title"
                                        name="title"
                                        label="Alert Title"
                                        placeholder="Enter alert title"
                                        onChange={handleChange("title")}
                                        onBlur={handleBlur("title")}
                                        value={values?.title || ""}
                                        error={Boolean(errors.title)}
                                        helperText={errors.title}
                                        fullWidth
                                        disabled={disabled}
                                        size="small"
                                    />
                                </Grid>

                                {/* Content */}
                                <Grid item xs={12}>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1, fontSize: "14px", color: "#454545", fontWeight: "500" }}
                                        >
                                            Alert Content
                                        </Typography>
                                        {disabled ? (
                                            <Box
                                                sx={{
                                                    border: "1px solid #e0e0e0",
                                                    borderRadius: 1,
                                                    p: 2,
                                                    minHeight: "200px",
                                                    backgroundColor: "#f5f5f5",
                                                }}
                                                dangerouslySetInnerHTML={{ __html: values?.content || "" }}
                                            />
                                        ) : (
                                            <RichTextEditor
                                                class="h-5"
                                                defaultValue={values?.content || ""}
                                                setValue={(data) => setFieldValue("content", data)}
                                            />
                                        )}
                                        {errors.content && (
                                            <Typography
                                                variant="caption"
                                                color="error"
                                                sx={{ mt: 1, display: "block" }}
                                            >
                                                {errors.content}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" sx={{ mt: 1, display: "block", color: "#666" }}>
                                            {disabled
                                                ? "Alert content preview"
                                                : "Use the rich text editor to format your alert content"}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Target Type */}
                                <Grid item xs={12}>
                                    <FormControl component="fieldset" disabled={disabled}>
                                        <FormLabel component="legend" sx={{ fontSize: "0.875rem", mb: 1 }}>
                                            Send Alert To
                                        </FormLabel>
                                        <RadioGroup
                                            row
                                            value={values.target_type}
                                            onChange={(e) => {
                                                setFieldValue("target_type", e.target.value);
                                                setFieldValue("target_roles", []);
                                                setFieldValue("target_users", []);
                                            }}
                                        >
                                            <FormControlLabel value="all" control={<Radio />} label="All Users" />
                                            <FormControlLabel
                                                value="roles"
                                                control={<Radio />}
                                                label="Specific Roles"
                                            />
                                            <FormControlLabel
                                                value="users"
                                                control={<Radio />}
                                                label="Specific Users"
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>

                                {/* Target Roles */}
                                {values.target_type === "roles" && (
                                    <Grid item xs={12}>
                                        <MultipleSelectJson
                                            label="Select Roles"
                                            value={values.target_roles || []}
                                            onChange={(value) => setFieldValue("target_roles", value)}
                                            options={roleOptions}
                                            disabled={disabled}
                                            error={Boolean(errors.target_roles)}
                                            helperText={errors.target_roles}
                                        />
                                    </Grid>
                                )}

                                {/* Target Users */}
                                {values.target_type === "users" && (
                                    <Grid item xs={12}>
                                        <MultipleSelectJson
                                            label="Select Users"
                                            value={values.target_users || []}
                                            onChange={(value) => setFieldValue("target_users", value)}
                                            options={userOptions}
                                            disabled={disabled}
                                            error={Boolean(errors.target_users)}
                                            helperText={errors.target_users}
                                        />
                                    </Grid>
                                )}

                                {/* Expiry Date */}
                                <Grid item xs={12}>
                                    <DatePickerComponent
                                        id="expiry_date"
                                        name="expiry_date"
                                        label="Expiry Date"
                                        value={values?.expiry_date || ""}
                                        onChange={(value) => setFieldValue("expiry_date", value)}
                                        error={Boolean(errors.expiry_date)}
                                        helperText={errors.expiry_date || "Alert will expire after this date"}
                                        disabled={disabled}
                                        minDate={new Date().toISOString().split("T")[0]}
                                    />
                                </Grid>

                                {/* Image Upload */}
                                {!disabled && (
                                    <Grid item xs={12}>
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Alert Images (Optional)
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<AddPhotoAlternateIcon />}
                                                disabled={uploadingImage}
                                                size="small"
                                            >
                                                {uploadingImage ? "Uploading..." : "Upload Image"}
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept="image/*"
                                                    onChange={handleImageUpload}
                                                />
                                            </Button>
                                            <Typography variant="caption" display="block" sx={{ mt: 1, color: "#666" }}>
                                                Max file size: 5MB. Supported formats: JPG, PNG, GIF
                                            </Typography>
                                        </Box>

                                        {/* Display uploaded images */}
                                        {uploadedImages && uploadedImages.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Grid container spacing={2}>
                                                    {uploadedImages.map((img, index) => (
                                                        <Grid item xs={6} md={4} key={index}>
                                                            <Box
                                                                sx={{
                                                                    position: "relative",
                                                                    borderRadius: 1,
                                                                    overflow: "hidden",
                                                                    border: "1px solid #e0e0e0",
                                                                }}
                                                            >
                                                                <img
                                                                    src={
                                                                        baseUrl +
                                                                        (typeof img.image_url === "string"
                                                                            ? img.image_url
                                                                            : img.image_url?.location ||
                                                                              img.image_url?.image_url)
                                                                    }
                                                                    alt={`Alert ${index + 1}`}
                                                                    style={{
                                                                        width: "100%",
                                                                        height: "120px",
                                                                        objectFit: "cover",
                                                                    }}
                                                                />
                                                                <IconButton
                                                                    size="small"
                                                                    sx={{
                                                                        position: "absolute",
                                                                        top: 4,
                                                                        right: 4,
                                                                        backgroundColor: "rgba(255,255,255,0.9)",
                                                                        "&:hover": {
                                                                            backgroundColor: "#fff",
                                                                        },
                                                                    }}
                                                                    onClick={() => handleRemoveImage(index)}
                                                                >
                                                                    <DeleteIcon fontSize="small" color="error" />
                                                                </IconButton>
                                                            </Box>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>
                                        )}
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>

                        {formType !== "View" && (
                            <Grid
                                flexShrink={0}
                                borderTop={1}
                                borderColor={"rgba(152, 188, 252, 0.16)"}
                                sx={{ padding: "8px 16px" }}
                            >
                                <Grid sx={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Button
                                            size="medium"
                                            type="submit"
                                            loading={addAlertLoading || updateAlertLoading}
                                            onClick={() => handleSubmit()}
                                        >
                                            {formType === "Add" ? "Create Alert" : "Update Alert"}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Drawer>
            )}
        </Formik>
    );
};
